import React, { FC, useState } from 'react';
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useNotesFeedV1InfiniteQuery } from '../healthWalletSlice';
import type { SectionProps } from './TabRenderer';
import { NotesSection } from '../NotesSection';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// Notes tab sections (V1 — NO design file yet; "adapt what we have to the
// overall design", Leo 2026-07-13). NO cards and NO detail dialog: notes are
// documents to READ — the existing NotesSection feed (expandable rows, lazy
// bodies, binary download) stays the list renderer; V1 adds the chrome (hero,
// explainer, palette) + SERVER-SIDE search and note-type bucket pills (the
// feed is paginated, so client-side filtering would silently search only the
// loaded pages). Buckets from the type-display vocabulary (probe 2026-07-13).
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };

const BUCKETS: Array<{ key: string; label: string }> = [
  { key: 'visit', label: 'Visit notes' },
  { key: 'lab', label: 'Lab reports' },
  { key: 'pathology', label: 'Pathology' }, // narrative Doc/Nar result Observations (+ any pathology reports) — pill shows only when present
  { key: 'imaging', label: 'Imaging' },
  { key: 'instructions', label: 'Instructions' },
  { key: 'procedure', label: 'Procedure notes' },
  { key: 'summary', label: 'Summaries' }, // whole-record CCD summaries (34133-9 / 60591-5 / summary-document) — kept, but out of the provider-note stream
  { key: 'other', label: 'Other' },
];

// Hero count tiles — same buckets as the pills, each with a palette color; rendered present-only (a tile shows
// only when the patient has notes in that bucket, so no "0 Pathology" boxes for patients who lack the category).
const BUCKET_TILES: Array<{ key: string; label: string; bg: string; fg: string }> = [
  { key: 'visit',        label: 'Visit notes',     bg: '#eef3fb', fg: '#2f5fb0' },
  { key: 'lab',          label: 'Lab reports',     bg: '#e6f6ec', fg: '#1a7f3c' },
  { key: 'pathology',    label: 'Pathology',       bg: '#f3eee6', fg: '#8a6a3a' },
  { key: 'imaging',      label: 'Imaging',         bg: '#eef2f2', fg: '#4a6b6b' },
  { key: 'instructions', label: 'Instructions',    bg: '#fdf0dd', fg: '#9a5b00' },
  { key: 'procedure',    label: 'Procedure notes', bg: '#efeaff', fg: '#6C4CE0' },
  { key: 'summary',      label: 'Summaries',       bg: '#eceef5', fg: '#3f4a6b' },
  { key: 'other',        label: 'Other',           bg: '#f0f0f3', fg: '#6b6b70' },
];

// --- 1. Hero (always unfiltered — its own cache entry; RTK keys by arg) --------
export const NotesHero: FC<SectionProps> = () => {
  const { data, isLoading } = useNotesFeedV1InfiniteQuery({});
  const first = data?.pages[0];
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const buckets = first?.buckets ?? {};
  const total = first?.total ?? 0;
  const tile = (key: string, value: number, label: string, bg: string, fg: string) => (
    <Box key={key} sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value.toLocaleString('en-US')}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  const bucketCounts = buckets as Record<string, number>;
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your record at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            Your record contains <strong>{total.toLocaleString('en-US')}</strong> notes and reports from your care team.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {BUCKET_TILES.filter((t) => (bucketCounts[t.key] ?? 0) > 0)
            .map((t) => tile(t.key, bucketCounts[t.key] ?? 0, t.label, t.bg, t.fg))}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer ---------------------------------------------------------------
export const NotesExplainer: FC<SectionProps> = () => (
  <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
    <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>About your notes</Typography>
        <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
          These are the notes, reports, and instructions your providers wrote — shown as documented, newest first.
          Use the filters to see one kind of note, or search by title. Notes open one at a time — scroll to read,
          then use the arrows to move to the next.
        </Typography>
      </Box>
    </Box>
  </Container>
);

// --- 3. Body: search + bucket pills + the existing feed renderer -----------------
export const NotesBody: FC<SectionProps> = () => {
  const [type, setType] = useState('');
  // Search DEFERRED (Leo 2026-07-13): titles/types are too generic to be worth searching ("Laboratory
  // report" ×358), and honest CONTENT search needs the notes-text extraction table (recorded item) —
  // per-query decode of ~1,100 bodies would be seconds-slow. The server-side p_q plumbing stays built
  // and dormant; the field renders as a disabled coming-soon placeholder.
  const { data, isLoading, isFetching, fetchNextPage } = useNotesFeedV1InfiniteQuery(
    type ? { type } : {}
  );
  const notes = data?.pages.flatMap((p) => p.notes) ?? [];
  const total = data?.pages[0]?.total ?? 0;
  const buckets = data?.pages[0]?.buckets ?? {};

  // Pill counts removed for cross-tab uniformity (Leo 2026-07-14) — Notes was the only tab showing
  // counts on its classifier pills; totals already live top-right in the hero. `buckets` still gates
  // which pills appear (a filter matching zero notes stays hidden).
  const pill = (key: string, label: string) => (
    <Box key={key} onClick={() => setType(key === type ? '' : key)} sx={{
      fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
      bgcolor: type === key ? V1_ACCENT : 'white', color: type === key ? 'white' : '#3a3a3c',
      border: `1px solid ${type === key ? V1_ACCENT : '#e0e0e6'}`, display: 'flex', alignItems: 'center', gap: 0.75,
    }}>
      {label}
    </Box>
  );

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value="" onChange={() => {}} placeholder="Search inside your notes — coming soon" disabled />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Box onClick={() => setType('')} sx={{
          fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
          bgcolor: type === '' ? V1_ACCENT : 'white', color: type === '' ? 'white' : '#3a3a3c',
          border: `1px solid ${type === '' ? V1_ACCENT : '#e0e0e6'}`,
        }}>All</Box>
        {BUCKETS.map((b) => ((buckets[b.key] ?? 0) > 0 ? pill(b.key, b.label) : null))}
      </Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Box>
      ) : notes.length === 0 ? (
        <Typography sx={{ p: '40px 26px', textAlign: 'center', color: '#8e8e93', fontSize: 15 }}>
          No notes match this filter.
        </Typography>
      ) : (
        // The existing feed renderer (expandable rows, lazy bodies, binary download) — reused as-is.
        <NotesSection notes={notes} total={total} onLoadMore={() => fetchNextPage()} loadingMore={isFetching} />
      )}
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          These notes are shown exactly as documented by your healthcare providers. They are not interpreted or summarized.
        </Typography>
      </Box>
    </Container>
  );
};
